import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Timer } from "lucide-react"
import type { GameQuestion, PlayerPowerUp } from '@/types/games'

interface MemoryPair {
  text: string
  pair: string
}

interface MemoryCard {
  id: number
  value: string
  text: string
  pairId: number
  isMatched: boolean
  isFlipped: boolean
}

interface MemoryGameProps {
  gameId: string
  question: GameQuestion
  powerUps?: PlayerPowerUp[]
  onComplete: (score: number, time: number) => void
  onUsePowerUp?: (powerUpId: string) => Promise<boolean>
}

export function MemoryGame({
  question,
  onComplete,
  gameId: _gameId,
  powerUps: _powerUps,
  onUsePowerUp: _onUsePowerUp
}: MemoryGameProps) {
  // Create pairs array from question answers
  const [cards, setCards] = useState<MemoryCard[]>(() => {
    const pairs = question.answers as MemoryPair[]
    const allCards = pairs.flatMap((pair, pairId) => [
      { 
        id: pairId * 2, 
        value: pair.text,
        text: pair.text, 
        pairId, 
        isMatched: false,
        isFlipped: false
      },
      { 
        id: pairId * 2 + 1, 
        value: pair.pair,
        text: pair.pair, 
        pairId, 
        isMatched: false,
        isFlipped: false
      }
    ])
    // Shuffle cards
    return allCards.sort(() => Math.random() - 0.5)
  })
  
  const [selectedCards, setSelectedCards] = useState<number[]>([])
  const [timeLeft, setTimeLeft] = useState(120)
  const [score, setScore] = useState(0)
  const [moves, setMoves] = useState(0)
  const [isChecking, setIsChecking] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0) {
          clearInterval(timer)
          onComplete(score, 120 - prev)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (selectedCards.length !== 2 || isChecking) return

    setIsChecking(true)
    const [firstId, secondId] = selectedCards
    const firstCard = cards.find(c => c.id === firstId)
    const secondCard = cards.find(c => c.id === secondId)

    if (!firstCard || !secondCard) return

    setMoves(m => m + 1)

    // Check for match after a short delay
    setTimeout(() => {
      if (firstCard.pairId === secondCard.pairId) {
        // Match found
        setCards(prev => prev.map(card => 
          card.id === firstId || card.id === secondId
            ? { ...card, isMatched: true }
            : card
        ))
        setScore(prev => prev + 1000)
      } else {
        // No match - flip cards back
        setCards(prev => prev.map(card => 
          card.id === firstId || card.id === secondId
            ? { ...card, isFlipped: false }
            : card
        ))
      }
      setSelectedCards([])
      setIsChecking(false)
    }, 1000)
  }, [selectedCards])

  const handleCardClick = (cardId: number) => {
    if (
      selectedCards.length === 2 || // Two cards already selected
      selectedCards.includes(cardId) || // Card already selected
      cards.find(c => c.id === cardId)?.isMatched || // Card already matched
      isChecking // Currently checking a pair
    ) {
      return
    }

    // Flip the card
    setCards(prev => prev.map(card =>
      card.id === cardId
        ? { ...card, isFlipped: true }
        : card
    ))

    setSelectedCards(prev => {
      if (prev.length === 2) return prev
      return [...prev, cardId]
    })
  }

  // Check if all pairs are matched
  const allMatched = cards.every(card => card.isMatched)

  useEffect(() => {
    if (allMatched) {
      // Add time bonus
      const timeBonus = Math.floor(timeLeft * 20) // Increased time bonus
      const finalScore = score + timeBonus
      setScore(finalScore)
      onComplete(score, 120 - timeLeft)
    }
  }, [allMatched])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Memory Match</span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4" />
              <span className={timeLeft <= 10 ? "text-destructive" : ""}>
                {timeLeft}s
              </span>
            </div>
            <span className="text-lg font-normal">
              Score: {score}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-4">
          {cards.map((card) => (
            <Button
              key={card.id}
              variant={card.isMatched ? "default" : card.isFlipped ? "secondary" : "outline"}
              className="h-24 text-lg transition-all"
              onClick={() => handleCardClick(card.id)}
              disabled={card.isMatched || isChecking}
            >
              {card.isFlipped || card.isMatched
                ? card.value
                : <span className="text-2xl">?</span>}
            </Button>
          ))}
        </div>
        <div className="mt-4 text-sm text-muted-foreground text-center">
          Moves: {moves}
        </div>
      </CardContent>
    </Card>
  )
}